import { Observable, Subject } from "rxjs";

import { ScrollOffset } from "./constants";
import { EasingFunction } from "./easing-functions";

export class ScrollInstructionSet {
  constructor(
    private instructions: ScrollInstruction[],
    private duration: number,
    private easingFn: EasingFunction,
    private requestAnimationFrame: (callback: () => void) => void,
    private getTime: () => number
  ) {}

  animate(): Observable<void> {
    let subject: Subject<void> = new Subject<void>();

    let startTime: number = this.getTime();

    let animation: () => void = () => {
      if (this.stopped) {
        subject.error(new Error("Scroll instruction set stopped"));
        subject.complete();
        return;
      }

      let now: number = this.getTime();
      let t: number = Math.min(this.duration, now - startTime) / this.duration;
      this.step(t);

      if (t < 1) {
        this.requestAnimationFrame(animation);
      } else {
        subject.next();
        subject.complete();
      }
    };

    this.requestAnimationFrame(animation);

    return subject;
  }

  private stopped: boolean = false;
  stop(): void {
    this.stopped = true;
  }

  private step(t: number): void {
    let p: number = this.easingFn(t);
    this.instructions.forEach((instruction) => {
      instruction.step(p);
    });
  }
}

export class ScrollInstruction {
  current: ScrollOffset;
  target: ScrollOffset;

  constructor(
    private scrollElement: HTMLElement,
    targetElement: HTMLElement,
    offsetLeft: number = 0,
    offsetTop: number = 0
  ) {
    let scrollElementRect: ClientRect = scrollElement.getBoundingClientRect(),
      targetElementRect: ClientRect = targetElement.getBoundingClientRect();

    this.current = {
      left: scrollElement.scrollLeft,
      top: scrollElement.scrollTop
    };

    this.target = {
      left:
        this.current.left! +
        targetElementRect.left -
        scrollElementRect.left -
        offsetLeft,
      top:
        this.current.top! +
        targetElementRect.top -
        scrollElementRect.top -
        offsetTop
    };
  }

  step(percent: number): void {
    this.scrollElement.scrollLeft = this.interpolate(
      this.current.left!,
      this.target.left!,
      percent
    );
    this.scrollElement.scrollTop = this.interpolate(
      this.current.top!,
      this.target.top!,
      percent
    );
  }

  private interpolate(start: number, end: number, percent: number): number {
    return (end - start) * percent + start;
  }
}
